import React from 'react';
import styled from 'styled-components';
import {
  ProgressIndicator,
  Text,
  Layout,
  TextLink,
  Divider,
} from '@oneaudi/unified-web-components';
import {
  SYS_SPACE_FIXED_200,
  SYS_SPACE_FIXED_300,
  SYS_SPACE_FIXED_400,
} from '@oneaudi/unified-web-common';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../../i18n/messages';
import { Dealer as DealerData } from '../../../models/dealer';
import mapContext from '../../../contexts/map-context';
import { SmallFiveStarRating } from '../FiveStarRating/SmallFiveStarRating';

const REDUCED_RESULTS_NUMBER = 2;

function getReducedDealersArray(dealers: DealerData[]): DealerData[] {
  return dealers.slice(0, REDUCED_RESULTS_NUMBER);
}

export type Props = {
  dealers: DealerData[];
  selectedPosition?: number;
  isLoading: boolean;
  isCompact: boolean;
  // eslint-disable-next-line no-unused-vars
  onDealerClick: (dealer: DealerData, pos: number) => void;
};
export const DealersList = ({
  dealers,
  selectedPosition,
  onDealerClick,
  isLoading,
  isCompact,
}: Props) => {
  const [showAll, setShowAll] = React.useState(false);
  const [dealersArray, setDealersArray] = React.useState(getReducedDealersArray(dealers));
  const showMoreMessage = useI18n(messages.autocompletion_show_more);
  const { dealerRatings } = mapContext.useMapState();
  React.useEffect(() => {
    setDealersArray(getReducedDealersArray(dealers));
    setShowAll(false);
  }, [dealers]);

  function onShowAllClicked() {
    setShowAll(true);
    setDealersArray([...dealers]);
  }

  React.useEffect(() => {
    if (selectedPosition && selectedPosition >= REDUCED_RESULTS_NUMBER) {
      onShowAllClicked();
    }
  }, [selectedPosition]);

  function handleOnDealerClick(dealer: DealerData, index: number) {
    const pos = index + 1;
    onDealerClick(dealer, pos);
  }

  return isLoading ? (
    <LoaderContainer>
      <ProgressIndicator variant="bar" data-test-id="loader-component" type="infinite" size="md" />
    </LoaderContainer>
  ) : (
    <Layout
      // margin={['24px', '28px', '24px', '28px']}
      data-test-id="dealers-list-component"
      direction="column"
    >
      <List role="list" data-testid="dealers-list">
        {dealersArray.map((dealer, i) => (
          <React.Fragment key={dealer.dealerId}>
            <Dealer
              onClick={() => {
                handleOnDealerClick(dealer, i);
              }}
              isCompact={isCompact}
              isSelected={i === selectedPosition}
              role="listitem"
              tabIndex={0}
              data-test-id="dealer-container"
            >
              <Layout key={dealer.dealerId} direction="column" justify="flex-start">
                <DealerName
                  forwardedAs="p"
                  variant={{ order: '1', style: 'normal', type: 'copy' }}
                  data-test-id="dealer-name1"
                  aria-selected={i === selectedPosition}
                  isLegacy={process.env.IS_LEGACY}
                >
                  {dealer.additionalData?.displayName
                    ? dealer.additionalData.displayName
                    : dealer.name}
                </DealerName>
                {dealerRatings?.[dealer.dealerId] >= 0 && (
                  <SmallFiveStarRating rating={dealerRatings?.[dealer.dealerId]} isValueDisplayed />
                )}
                <DealerAddress
                  forwardedAs="span"
                  variant={{ order: '2', style: 'normal', type: 'copy' }}
                  data-test-id="dealer-address"
                  isLegacy={process.env.IS_LEGACY}
                >
                  {dealer.address.join(', ')}
                </DealerAddress>
              </Layout>
            </Dealer>
            <Divider />
          </React.Fragment>
        ))}
      </List>
      {dealers.length > 2 && !showAll && (
        <MoreButton
          variant="standalone"
          isCompact={isCompact}
          onClick={() => onShowAllClicked()}
          data-testid="more-button"
          icon="add"
          iconPosition="leading"
          size="md"
        >
          {showMoreMessage}
        </MoreButton>
      )}
    </Layout>
  );
};

const LoaderContainer = styled.div`
  padding: ${SYS_SPACE_FIXED_300};
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const DealerName = styled(Text)<{ isLegacy: any }>`
  color: ${(props) => (props.isLegacy ? '#000' : '#fff')};
`;

const Dealer = styled.li<{ isCompact: boolean; isSelected: boolean }>`
  cursor: pointer;
  background-color: ${(props) => (props.isSelected ? `#f2f2f2` : 'transparent')};

  margin: ${SYS_SPACE_FIXED_200} 0;

  &:hover ${DealerName} {
    color: #4c4c4c;
  }
`;

const DealerAddress = styled(Text)<{ isLegacy?: any }>`
  color: ${(props) => (props.isLegacy ? '#666666' : '#9FA1A3')};
`;

const MoreButton = styled(TextLink)<{ isCompact: boolean }>`
  width: 100%;
  justify-content: flex-start;
  text-decoration: none;
  margin: ${SYS_SPACE_FIXED_400} 0 0;
`;
